import { CredentialsProviderSchema } from '@org-avp/models';
import { z } from 'zod';
/**
 * Required configuration for using AWS Kinesis as signaling solution
 */
const AwsKinesisSignalingSchema = z.object({
    credentialsProvider: CredentialsProviderSchema,
    channelName: z.string(),
    clientId: z.string()
});
/**
 * Required configuration for using 3DWS Cirrus as signaling solution
 *  signalingUrl: Url of the signaling server, e.g. wss://my-signaling-server.com
 */
const CirrusSignalingSchema = z.object({
    signalingUrl: z.string()
});
const SignalingOptionsSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('aws'), aws: AwsKinesisSignalingSchema }),
    z.object({ type: z.literal('cirrus'), cirrus: CirrusSignalingSchema })
]);
/**
 * Options for automatic connection retry during signaling channel or peer connection error.
 * Reconnecting for Cirrus connections only works if there are at least two streaming participants (e.g. for NeRD or 3D Remote).
 * If there is only one participant (e.g. on a brand.country website), the connection streaming session will be closed the Cirrus server.
 */
const RetryOptionsSchema = z.object({
    /**
     * Maximum attempts to try to reconnect
     */
    maxTries: z.number().gte(0).default(1),
    /**
     * Delay in ms between the reconnect attempts
     */
    retryDelayInMs: z.number().gt(0).default(10000)
});
/**
 * Main configuration object of the AVP-WebRTC-Player library containing initialization options.
 */
export const ConfigurationSchema = z.object({
    /**
     * The ID of the parent HTML element where the streaming video element will be attached.
     * If neither htmlContainerId nor htmlContainerReference is provided then no video element will be spawned.
     */
    htmlContainerId: z.string().optional(),
    /**
     * The ID of the parent HTML element where the streaming video element will be attached.
     * If neither htmlContainerId nor htmlContainerReference is provided then no video element will be spawned.
     */
    htmlContainerReference: z
        .custom((value) => value instanceof HTMLElement, { message: 'Invalid HTMLElement' })
        .optional(),
    /**
     * The mode in which the stream will be displayed within the parent HTML element.
     * Mimics the behaviour of the CSS property [object-fit](https://developer.mozilla.org/en-US/docs/Web/CSS/object-fit).
     * @default "contain"
     */
    displayMode: z.union([z.literal('cover'), z.literal('contain')]).default('contain'),
    /**
     * Disables video and audio streams for use-cases where only a data-channel is required.
     */
    dataChannelOnly: z.boolean().default(false),
    /**
     * Provides different options for signaling. Only one of the options should be configured.
     * @see CirrusSignaling
     * @see AwsKinesisSignaling
     */
    signalingOptions: SignalingOptionsSchema,
    retryOptions: RetryOptionsSchema.optional()
});
//# sourceMappingURL=model.js.map